import { Component, DoCheck, Input, OnInit } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { CatalogService } from "../../../../../Services/CatalogManagement/catalog.service";
import { LoginService } from "../../../../../Services/login.service";
import { Catalog } from "../../../../../Model/Catalog/Catalog";
import { DataSourcesService } from "../../../../../Services/DataProcess/datasources.service";
import { ImportContainer } from "../../../../../Model/dataProcess/importContainer";
import { ProductDataSource } from "../../../../../Model/dataProcess/productDataSource";
import { ManufacturerMapping } from "../../../../../Model/dataProcess/manufacturerMapping";
import { manualMappingOrigin } from "../../../../../Model/dataProcess/mappingOrigin";
import { SystemService } from "../../../../../Services/system.service";
import { TranslateService } from "@ngx-translate/core";
import { Address } from "../../../../../Model/Catalog/Address";
import { DataSourceMapping } from "../../../../../Model/Catalog/DataSourceMapping";
import { ManufacturerService } from "../../../../../Services/DataProcess/manufacturer.service";

export interface DataSourcePart {

    name?: string | null;
    status: 'mapped' | 'unmapped' | 'none';
    selectedId?: string | null;
    readonly: boolean;
}

export interface CatalogMappingRow {
    id: number;
    importManufacturerName?: string;
    datasource?: DataSourcePart;
}

type FilterKey = 'showMapped' | 'showUnmapped';

@Component({
    selector: 'np-manufacturer-mapping-tab',
    templateUrl: './np-manufacturer-mapping-tab.component.html',
    styleUrl: './np-manufacturer-mapping-tab.component.css',
    standalone: false
})
export class NpManufacturerMappingTabComponent implements OnInit, DoCheck {

    @Input() container!: ImportContainer;

    private previousActiveCatalogId: string;
    private isUpdatingActiveCatalog = false;

    importCatalogManufactureNames: string[] = [];
    importCatalog: Catalog = undefined;

    dataSources: ProductDataSource[] = []

    showMapped = true;
    showUnmapped = true;
    aiMappingRunning = false;
    existingMappings: ManufacturerMapping[] = [];

    newAddedDatasource: ProductDataSource;
    types: string[] = ['Manufacturer', 'Supplier'];
    popupVisible: boolean = false;

    rows: CatalogMappingRow[] = [];
    allRows: CatalogMappingRow[] = [];

    constructor(
        public loginService: LoginService,
        public catalogService: CatalogService,
        public dataSourceService: DataSourcesService,
        public manufacturerService: ManufacturerService,
        private systemService: SystemService,
        private translate: TranslateService,) {
        this.buildRows = this.buildRows.bind(this);
    }

    async ngOnInit() {
        this.previousActiveCatalogId = this.container?.activeCatalogId;
        await this.getAvailableDataSources();
        await this.getImportCatalog();
    }

    ngDoCheck() {
        const currentId = this.container?.activeCatalogId;
        if (currentId !== this.previousActiveCatalogId && !this.isUpdatingActiveCatalog) {
            this.previousActiveCatalogId = currentId;
            if (currentId) {
                this.isUpdatingActiveCatalog = true;
                this.getImportCatalog().finally(() => this.isUpdatingActiveCatalog = false);
            }
        }
    }

    dropDownCfg = {
        toolbarItems: [
            {
                location: 'after',
                template: 'addSourceToolbarButton'
            }
        ]
    };

    async addNewSource() {
        var newDataSourceAddress = new Address();
        var newDataSourceImporterAddress = new Address();
        this.newAddedDatasource = {
            addressObj: newDataSourceAddress,
            importerAddress: newDataSourceImporterAddress
        } as ProductDataSource;
        this.popupVisible = true;
    }

    async onEditSaved($event) {
        this.dataSources = await this.dataSourceService.getAll(this.loginService.currentCustomer.id);
        this.ensureEmptyDataSource();
    }

    async getAvailableDataSources() {
        this.dataSources = await this.dataSourceService.getAll(this.loginService.currentCustomer.id);
        this.ensureEmptyDataSource();
    }

    private ensureEmptyDataSource(): void {
        const hasEmpty = this.dataSources.some(x => x.id == null || x.id === '');
        if (hasEmpty) return;

        const empty = { id: '', name: '' } as ProductDataSource;
        this.dataSources = [empty, ...this.dataSources];
    }

    get filteredRows(): CatalogMappingRow[] {
        return this.rows;
    }

    async getImportCatalogManufactureNames() {
        this.importCatalogManufactureNames = (await this.dataSourceService
            .getAllCatalogManufacturerNames(this.importCatalog.id, this.loginService.currentCustomer.id)).map(x => x.manufacturerName);

        this.buildRows();
    }

    buildRows() {
        this.allRows = [];

        if (this.importCatalogManufactureNames.length > 0) {
            this.importCatalogManufactureNames.forEach((manufacturerName) => {
                let existingRow = this.allRows.find(x => x.importManufacturerName == manufacturerName);
                if (existingRow) {
                    existingRow.importManufacturerName = manufacturerName;
                } else {
                    this.allRows.push({
                        datasource: { name: "", status: "unmapped", selectedId: "", readonly: false } as DataSourcePart,
                        id: Math.max(...this.allRows.map(r => r.id)) + 1,
                        importManufacturerName: manufacturerName,
                    });
                }
            })
        }
        if (this.existingMappings.length > 0) {
            this.existingMappings.forEach((item) => {
                let existingRow = this.allRows.find(x => x.importManufacturerName == item.dataSourceMapping.manufacturerName);
                if (existingRow) {
                    existingRow.datasource.status = "mapped";
                    existingRow.datasource.selectedId = item.dataSourceMapping.currentId;
                }
            });
        }
        this.allRows.forEach((row) => {
            if (row.importManufacturerName == "")
                row.datasource.readonly = true;
        });

        this.filterRows();
    }

    private filterRows() {
        if (this.showMapped &&
            this.showUnmapped) {
            this.rows = [...this.allRows];
            return;
        }

        if (!this.showMapped &&
            !this.showUnmapped) {
            this.rows = [];
            return;
        }

        this.rows = this.allRows.filter(row =>
            (this.showMapped && row.datasource.status === "mapped") ||
            (this.showUnmapped && row.datasource.status === "unmapped")
        );
    }

    async onAutoMap() {
        const openNames = this.allRows
            .filter(row => row.importManufacturerName && row.datasource.status === "unmapped")
            .map(row => row.importManufacturerName);

        if (openNames.length === 0) {
            this.systemService.notifyInfo(this.translate.instant('Es konnte keine Zuordnung gefunden werden.'), 3000);
            return;
        }

        this.aiMappingRunning = true;
        try {
            const result = await this.manufacturerService.suggestCatalogMapping(openNames, this.container.id, this.importCatalog.id);
            const mapped = result.suggestions.filter(suggestion => suggestion.isMapped);

            if (mapped.length > 0)
                await this.getExistingManufacturerMappings();

            if (result.aiBudgetExceeded)
                this.systemService.notifyWarning(this.translate.instant('Das KI-Kostenlimit ist erreicht. Die restlichen Einträge wurden nicht automatisch zugeordnet.'), 6000);
            else if (mapped.length === 0)
                this.systemService.notifyInfo(this.translate.instant('Es konnte keine Zuordnung gefunden werden.'), 3000);
        } catch (e) {
            this.notifySuggestionFailure(e);
        } finally {
            this.aiMappingRunning = false;
        }
    }

    private notifySuggestionFailure(e: any) {
        if (e?.status === 400 && typeof e.error === 'string') {
            this.systemService.notifyWarning(e.error, 6000);
            return;
        }
        if (e?.status === 429) {
            this.systemService.notifyWarning(this.translate.instant('Die automatische Zuordnung läuft bereits. Bitte warten.'), 4000);
            return;
        }
        this.systemService.notifyWarning(this.translate.instant('Die automatische Zuordnung ist fehlgeschlagen.'), 4000);
    }

    async onDataSourceChanged(e: any, row: CatalogMappingRow) {
        const datasourceId = e.value;
        const manufacturerName = row.importManufacturerName;
        const catalogId = this.importCatalog.id;
        const existingMapping =
            this.existingMappings.find(x =>
                x.dataSourceMapping.manufacturerName == manufacturerName &&
                x.catalogId == catalogId
            );
        let existingMappingId = "";
        let originalId = datasourceId;
        if (existingMapping) {
            existingMappingId = existingMapping.id;
            originalId = existingMapping.dataSourceMapping.originalId;
        }
        const dataSourceMapping = new DataSourceMapping(manufacturerName, originalId, datasourceId);
        const manufacturerMapping = new ManufacturerMapping(existingMappingId, this.container.id, catalogId, dataSourceMapping, manualMappingOrigin());

        if ((datasourceId == null || datasourceId === '') && existingMappingId != "")
            await this.manufacturerService.deleteCatalogMapping(manufacturerMapping, this.loginService.currentCustomer.id);
        else
            await this.manufacturerService.saveCatalogMapping(manufacturerMapping, this.loginService.currentCustomer.id);

        await this.getExistingManufacturerMappings();
    }

    private async getExistingManufacturerMappings() {
        this.existingMappings =
            await this.manufacturerService.getCatalogMappings(this.importCatalog.id, this.loginService.currentCustomer.id);
        this.buildRows();
    }

    protected onToggleFilter(key: FilterKey, e: { value: boolean }) {
        this[key] = e.value;
        this.filterRows();
    }

    private async getImportCatalog(): Promise<void> {
        this.importCatalog = await firstValueFrom(
            this.catalogService.getCatalog(this.container.activeCatalogId, this.loginService.currentCustomer.id)
        );
        await this.getImportCatalogManufactureNames();
        await this.getExistingManufacturerMappings();
    }
}
